/**
 * WHAT IS WRONG WITH A BLOCK'S RECALLS — decided once, for both surfaces that ask.
 *
 * These were hooks and nothing else, which meant they ran in a block EDITOR and nowhere else.
 * `experience-problems` had no alias or reference logic at all, so a dead recall — the answer
 * was deleted, or is never reached before here — was drawn on the card and invisible to the
 * experience error panel and to the publication gate, which counts `severity === 'error'` over
 * the board's rows. Both of these are errors. Both shipped.
 *
 * It is the same defect the variables content rule had, and it takes the same remedy: the
 * decision moves to a leaf module and the hooks become callers of it. The leaf is structural
 * rather than tidy — `block-issue-dispatch` imports all seventeen block files and every one of
 * them imports `IssuesPanel`, so a check reachable from both that lived in either would close an
 * import cycle, which is the shape behind the HMR rebuild loop.
 *
 * ## Per usage, site and all
 *
 * Every status here is asked with `{ atExit }`. A same-task member is unreachable from a block
 * and perfectly reachable from that task's exit, and a status computed without the site calls an
 * exit's working reference dead — which is exactly what `answerRefCount` did until `aefe363a`.
 */
import { aliasStatus, collectAliasUsages } from '@agavi/blocks';


import type { BlockIssue } from '@/components/task/block-issues';
import type { AnswerRefAlias, RefCensusTask } from '@agavi/blocks';

import { recallAllowedBetween, recallAllowedInBlock } from '@/lib/recall-rungs';

/** The four things both checks need, named once rather than threaded as four arguments twice. */
export interface RecallIssueScope {
  tasks: RefCensusTask[];
  aliases: readonly AnswerRefAlias[];
  currentTaskId: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
  /**
   * The Answer Recall rung, for the check that asks whether a recall may be in THIS BLOCK TYPE
   * at all. Absent means the caller cannot know it and that check does not run — the honest
   * default, since inventing a high rung hides a defect and a low one invents errors.
   */
  rung?: number;
}

/**
 * A RECALL WHERE THE RUNG DOES NOT SUPPORT ONE.
 *
 * The affordance decides whether to OFFER the control; it cannot decide whether a chip already
 * present is valid, because offering is not the only way one arrives. A chip pasted into a block
 * above the rung is reconstituted by the extension's `parseHTML`, duplicating a block carries
 * one, and lowering the rung after authoring leaves every existing chip exactly where it was.
 *
 * Recall has one bypass fewer than variables: a token is `[ara_<opaque id>]`, so unlike a typed
 * `{dip}` nobody can write one by hand. The rest apply, so the rule is still about what is
 * SUPPORTED rather than about how it got there.
 *
 * Checked against the DESTINATION only. The source half is applied to the offerable list, and a
 * chip that is here at all names a source that was offerable when it was inserted.
 */
export function recallRungIssues(
  block: { id: string; type: string } | undefined, s: RecallIssueScope,
): BlockIssue[] {
  if (!block || s.rung === undefined) return [];
  const used = collectAliasUsages(s.tasks).filter(u => u.inBlockId === block.id);
  if (used.length === 0) return [];

  /*
   * THE DESTINATION FIRST, because it condemns every recall in the block at once and naming
   * them one at a time would be the same sentence repeated.
   */
  if (!recallAllowedInBlock(s.rung, block.type)) {
    return [{
      key: 'recall-not-supported-here',
      severity: 'error',
      kind: 'defect',
      category: s.t('blocks.answerRef.issueCategory'),
      detail: s.t('blocks.answerRef.notSupportedInBlock', { count: used.length }),
    }];
  }

  /*
   * AND THEN THE SOURCE, which the destination check cannot stand in for.
   *
   * This is where recall differs from variables: it widens along two axes. A Numerical block
   * recalling an MCQ answer is authored at the top rung and, dropped to rung 1, keeps a
   * destination the rung still admits and a SOURCE it does not. Checking the destination alone
   * returned early on exactly that block and said nothing — the lowered-rung case this rule
   * exists for, missed by the rule.
   *
   * Members share a block type by construction (`typeMismatch` refuses an alias that spans two),
   * so one offending member condemns the name; the sweep still walks them all rather than
   * trusting that invariant from another module.
   */
  const stranded = new Set<string>();
  for (const usage of used) {
    const alias = s.aliases.find(a => a.id === usage.aliasId);
    if (!alias) continue;   // a nameless chip is `brokenRefIssues`' to report, not this one
    for (const m of alias.members) {
      const source = s.tasks.find(tk => tk.id === m.taskId)?.blocks?.find(b => b.id === m.blockId);
      // A member whose block is gone is broken rather than out of rung, and is reported there.
      if (!source) continue;
      if (!recallAllowedBetween(s.rung, source.type, block.type)) {
        stranded.add(alias.name ?? s.t('blocks.answerRef.unnamedSource'));
      }
    }
  }
  if (stranded.size === 0) return [];

  return [{
    key: 'recall-source-not-supported',
    severity: 'error',
    kind: 'defect',
    category: s.t('blocks.answerRef.issueCategory'),
    detail: s.t('blocks.answerRef.sourceNotSupported', {
      count: stranded.size, names: [...stranded].join(', '),
    }),
  }];
}

/**
 * CHIPS IN THIS BLOCK THAT CAN NEVER RESOLVE.
 *
 * One issue however many are dead, because the fix is one visit to one panel. A row per chip
 * would be the same sentence three times in a list teachers already stop reading.
 */
export function brokenRefIssues(blockId: string | undefined, s: RecallIssueScope): BlockIssue[] {
  if (!blockId) return [];
  const broken = collectAliasUsages(s.tasks)
    .filter(u => u.inBlockId === blockId)
    .filter(u => {
      const alias = s.aliases.find(a => a.id === u.aliasId);
      return !alias
        || aliasStatus(alias, s.currentTaskId, s.tasks, { atExit: !!u.inExitSlot }) === 'broken';
    });
  if (broken.length === 0) return [];

  return [{
    key: 'answer-ref-broken',
    severity: 'error',
    kind: 'defect',
    category: s.t('blocks.answerRef.issueCategory'),
    detail: s.t('blocks.answerRef.issueBroken', { count: broken.length }),
  }];
}

/**
 * A RECALL IN A CALCULATION THAT SOME STUDENTS NEVER ANSWER.
 *
 * `conditional` carries no warning anywhere else, deliberately: a reference to a pathway answer
 * is as often legitimate as not, and in PROSE it degrades — the chip shows its fallback, or
 * nothing, and the sentence still reads.
 *
 * ARITHMETIC HAS NO FALLBACK. `evaluateEquation` returns null when a recall has no value rather
 * than guessing, so for a student who took the other route the answer key is simply UNSET,
 * silently. `inFormula` is already exactly the question — a formula field stores `[ara_…]` where
 * prose stores a chip span — and it covers an answer equation and a branch condition together.
 */
export function conditionalFormulaRefIssues(
  blockId: string | undefined, s: RecallIssueScope,
): BlockIssue[] {
  if (!blockId) return [];
  const names = new Set<string>();
  for (const usage of collectAliasUsages(s.tasks)) {
    if (usage.inBlockId !== blockId || !usage.inFormula) continue;
    const alias = s.aliases.find(a => a.id === usage.aliasId);
    /* A BROKEN one is `brokenRefIssues`' to report, and saying it here as well would be two
       sentences about one chip at two precisions. */
    if (!alias
      || aliasStatus(alias, s.currentTaskId, s.tasks, { atExit: !!usage.inExitSlot }) !== 'conditional') {
      continue;
    }
    names.add(alias.name ?? s.t('blocks.answerRef.unnamedSource'));
  }
  if (names.size === 0) return [];

  return [{
    key: 'answer-ref-conditional-in-formula',
    severity: 'error',
    kind: 'defect',
    category: s.t('blocks.answerRef.issueCategory'),
    detail: s.t('blocks.answerRef.issueConditionalInFormula', {
      count: names.size, names: [...names].join(', '),
    }),
  }];
}

/** All of them, which is what a surface asking about a whole block wants. */
export function recallIssuesFor(
  block: { id: string; type: string } | undefined, s: RecallIssueScope,
): BlockIssue[] {
  return [
    ...brokenRefIssues(block?.id, s),
    ...conditionalFormulaRefIssues(block?.id, s),
    ...recallRungIssues(block, s),
  ];
}
